import type { BarState, TimelineBar } from "./timelineModel.js";
import { berlinerTag, tagBeschriftung, tageZwischen } from "./ticketFormat.js";
import { assigneePopup, dueMark } from "./hierarchy.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { hasEffort, shownEffort } from "./effort.js";
import { EFFORT_TEXT, effortLines } from "./effortTexts.js";
import { DUE_ITEM_TEXT } from "../termine/dueItemTexts.js";
import type { DueTextItem } from "../termine/dueItemTexts.js";

/**
 * TEXTE DER ZEITACHSE (Spec Plan-Ansicht „Skizze“, „Interaktion“, „Telefon“, „Leistung“; S11, S15, S20) — EINE Stelle
 * für alle Anwendertexte der Ansicht, Schlüssel englisch. Popups sind reiner Text mit Zeilenumbrüchen (`\n`, Plan P4):
 * Titel aus Tickets werden so nie HTML. Die Terminebene (Spec D24) nimmt Quellwörter, Popup- und Vorlesezeilen aus
 * `views/termine/dueItemTexts.ts` — die Richtung ist `tickets` → `termine`, nie umgekehrt.
 */

const count = (n: number, one: string, many: string) => (n === 1 ? `1 ${one}` : `${n} ${many}`);
const NUMBER = new Intl.NumberFormat("de-DE");

/** „05.10." — ein Kalendertag ohne Wochentag (Achse, Fenster, Vorlesestimme). */
export function shortDay(day: string): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}.`;
}

const STATES: Readonly<Record<BarState, string>> = {
  OVERDUE: "überfällig", DUE_TODAY: "heute fällig", OPEN: "offen", WAITING: "wartend", DONE: "erledigt",
};
const IDLE = "frei";
const DUE_BEFORE_RESUBMIT = "Fällig bis liegt vor der Wiedervorlage";
const OPEN_END = "offenes Ende";
const OPEN_IN_TICKET = "im Ticket öffnen";
const overdue = (n: number) => `${n} überfällig`;
const alsoUnder = (team: string) => `auch unter ${team}`;
const childrenMark = (n: number) => count(n, "Teilaufgabe", "Teilaufgaben");
const resubmitSpoken = (day: string) => `Wiedervorlage ${shortDay(day)}`;
/** Privates Ticket (S10, F134): derselbe Wortlaut wie `TeamChip` an Karte, Liste und Steckbrief — auf der Fläche steht
 *  kein Schloss (Bild ohne Text), Popup und Vorlesestimme nennen es (Aufgabe 8). */
const privateTeam = (b: TimelineBar): string | null => (b.card.team === null ? null : `Privat: Team ${b.card.team.name}`);

/** Der Zeitraum des Balkens zum Vorlesen: „02.11. bis 13.11.“, „am 25.10.“, „seit 10.10., offenes Ende“; künftige
 *  Wiedervorlage ohne Fällig bis (Tagesmarke, Ruling M1): „Wiedervorlage 25.10.“. */
function spokenRange(b: TimelineBar): string {
  if (b.openEnd) return `seit ${shortDay(b.from)}, ${OPEN_END}`;
  if (b.card.dueDate === null && b.state !== "DONE") return resubmitSpoken(b.from);
  return b.from === b.to ? `am ${shortDay(b.from)}` : `${shortDay(b.from)} bis ${shortDay(b.to)}`;
}

/** Die Zustandszeile des Popups (S15): „überfällig seit 5 T · fällig Mo 12.10.“, „offen · fällig Fr 30.10.“, „erledigt Do 15.10.“. */
function stateLine(b: TimelineBar, heute: string): string {
  const c = b.card;
  if (b.state === "DONE") return c.doneAt === null ? STATES.DONE : `${STATES.DONE} ${tagBeschriftung(berlinerTag(c.doneAt))}`;
  if (b.state === "DUE_TODAY") return STATES.DUE_TODAY;
  const due = dueMark(c.dueDate, heute, false)?.text ?? (b.openEnd ? OPEN_END : "");
  const state = b.state === "OVERDUE" ? `${STATES.OVERDUE} seit ${tageZwischen(c.dueDate!, heute)} T` : STATES[b.state];
  return due === "" ? state : `${state} · ${due}`;
}

/** A22: „Aufwand 4,5 von 6 Stunden, 75 Prozent“ aus den angezeigten Werten, am Eltern-Balken mit „…, inkl. Teilaufgaben“
 *  (570, Review G2); ohne Plan und Buchung "". */
function effortSpoken(b: TimelineBar): string {
  const s = shownEffort(b.card);
  return !hasEffort(s) ? "" : (s.sum ? EFFORT_TEXT.spokenSum : EFFORT_TEXT.spoken)(s.plan, s.worked);
}

/** Die letzte Popup-Zeile zu Teilaufgaben: Fortschritt am Eltern-Balken, am Enkel „n Teilaufgaben · im Ticket öffnen“ (P10). */
function childrenLine(b: TimelineBar): string | null {
  if (b.children === 0) return null;
  return b.canExpand ? HIERARCHY_TEXT.progressSpoken(b.card.subtaskDone, b.children)
    : `${childrenMark(b.children)} · ${OPEN_IN_TICKET}`;
}

export const TIMELINE_TEXT = {
  tab: "Zeitachse",
  /** Pfad im Ticketdialog über der Zeitachse (wie „Tickets › Board“). */
  path: "Tickets › Zeitachse",
  toolbarName: "Werkzeuge der Zeitachse",
  newTask: "+ Aufgabe",
  stageGroup: "Zeitraum",
  /** Name der Segmente Tag | Woche | Monat | Quartal in der Zeile „Zeitraum“ (Ruling 548). */
  stageChoice: "Stufe",
  stages: { DAY: "Tag", WEEK: "Woche", MONTH: "Monat", QUARTER: "Quartal" },
  free: "frei",
  today: "Heute",
  back: "Zurück",
  forward: "Weiter",
  frame: "Zeitraum aufziehen",
  now: "jetzt",
  noTeam: "Ohne Team",
  teamRow: "ohne Zuständigen",
  unassignedRow: "nicht zugewiesen",
  idle: IDLE,
  states: STATES,
  legendName: "Legende der Zeitachse",
  legendOpenEnd: OPEN_END,
  legendLead: "Vorlauf",
  legendDuplicate: "auch in anderer Gruppe",
  loadError: "Die Zeitachse konnte nicht geladen werden.",
  openInTicket: OPEN_IN_TICKET,
  dueBeforeResubmit: DUE_BEFORE_RESUBMIT,
  resubmitSpoken,
  more: (n: number) => `+${n}`,
  /** „+2: BS-T-7, BS-T-8“ — Popup am Kennzeichen „+n“ und Ansage beim Wandern über die verborgenen Balken (S8). */
  moreList: (numbers: readonly string[]) => `+${numbers.length}: ${numbers.join(", ")}`,
  /** Name des „+n“-Knopfs und Ansage an einer Personenzeile — nennt die Aktion (Enter bzw. Klick: Liste mit der Person). */
  moreOpen: (numbers: readonly string[]) => `+${numbers.length}: ${numbers.join(", ")} – in der Liste zeigen`,
  overdue,
  alsoUnder,
  limit: (n: number) => `${NUMBER.format(n)} Tickets im Stand – bitte Filter eingrenzen`,
  childrenMark,
  calendarWeek: (week: number, day: string) => `KW ${week} · ${shortDay(day)}`,
  // ── Terminebene (D24, T19) ──
  milestonesGroup: "Meilensteine",
  /** Kopf einer Quellgruppe: das Wort des Chips („Rechnungen“). */
  sourceGroup: DUE_ITEM_TEXT.chipLabel,
  dueOverdue: DUE_ITEM_TEXT.sectionOverdue,
  legendMilestone: "Meilenstein",
  legendDue: "Fälligkeit",
  newMilestone: DUE_ITEM_TEXT.newMilestone,
  dueWindowLimit: "Termine: nur die ersten 400 Tage des Zeitraums.",
  dueLimit: (n: number) => `${NUMBER.format(n)} Termine im Zeitraum – bitte Quellen oder Filter eingrenzen.`,
  /** Sofort-Popup an der Marke (D24): Quellwort und Nummer, Titel, Partner · Unternehmen, Tag mit Zustand, Betrag,
   *  Fortschritt, Ticket — Zeilen mit `\n`, kein HTML. */
  dueTooltip: (item: DueTextItem, heute: string) => DUE_ITEM_TEXT.popupLines(item, heute).join("\n"),
  /** Zugänglicher Name einer Terminzeile: „BS, 2 Termine, 1 überfällig, 1 weiter“. */
  dueRowName: (label: string, n: number, late: number, hidden = 0) => [label, count(n, "Termin", "Termine"),
    late > 0 ? overdue(late) : "", hidden > 0 ? count(hidden, "weiter", "weitere") : ""].filter((t) => t !== "").join(", "),
  /** Zugänglicher Name des Kopfs einer Termingruppe: „Rechnungen, 3 Termine, 1 überfällig“. */
  dueGroupName: (label: string, n: number, late: number) =>
    [label, count(n, "Termin", "Termine"), late > 0 ? overdue(late) : ""].filter((t) => t !== "").join(", "),
  /** P8: „26.10. – 29.11.2026“, über den Jahreswechsel „15.12.2026 – 18.01.2027“. */
  windowText: (from: string, to: string) =>
    `${shortDay(from)}${from.slice(0, 4) === to.slice(0, 4) ? "" : from.slice(0, 4)} – ${shortDay(to)}${to.slice(0, 4)}`,
  /** Zugänglicher Name einer Zeile: „Bernd Board, 7 Tickets, 1 überfällig, 1 weiter“; ohne Ticket „…, frei“. Zahlen
   *  aus dem geladenen Stand (`row.loaded`, `row.loadedOverdue`, Ruling M2), „weiter“ = `row.hidden.length`. */
  rowName: (name: string, tickets: number, late: number, hidden = 0) => [name,
    tickets === 0 ? IDLE : count(tickets, "Ticket", "Tickets"),
    late > 0 ? overdue(late) : "",
    hidden > 0 ? count(hidden, "weiter", "weitere") : ""].filter((t) => t !== "").join(", "),
  /** Zugänglicher Name des Gruppenkopfs: „BH Buchhaltung, 3 Tickets, 1 überfällig“ (`group.loaded`, `group.loadedOverdue`);
   *  das sichtbare Kürzel steht vorn, damit Sprachsteuerung den Knopf darüber findet (WCAG 2.5.3). */
  groupName: (label: string, tickets: number, late: number, shortCode?: string) =>
    [shortCode === undefined ? label : `${shortCode} ${label}`, count(tickets, "Ticket", "Tickets"), late > 0 ? overdue(late) : ""]
      .filter((t) => t !== "").join(", "),
  /** Live-Text der Tastaturwanderung (S15): „ME-T-0057 Konzept schreiben, offen, 02.11. bis 13.11.“, privat mit
   *  „Privat: Team ‹Name›“ hinter dem Titel, mit Aufwand „…, Aufwand 4,5 von 6 Stunden, 75 Prozent“ (A22); bei einer
   *  Wiederholung (533) mit „auch unter ‹Hauptgruppe›“, wenn der Aufrufer den Namen der Hauptgruppe kennt. */
  barSpoken: (b: TimelineBar, heute: string, primaryGroup?: string) => [`${b.card.displayNumber} ${b.card.title}`,
    privateTeam(b) ?? "",
    b.state === "OVERDUE" ? `${STATES.OVERDUE} seit ${count(tageZwischen(b.card.dueDate!, heute), "Tag", "Tagen")}` : STATES[b.state],
    spokenRange(b),
    effortSpoken(b),
    b.isDuplicate && primaryGroup !== undefined ? alsoUnder(primaryGroup) : ""].filter((t) => t !== "").join(", "),
  /** Sofort-Popup am Balken (S15, P4, P15): Zeilen mit `\n`, kein HTML. `primaryGroup` = Name der Hauptgruppe bei
   *  `isDuplicate` — die erste Zeile lautet dann „auch unter ‹Team›“. Hinter der Zustandszeile der Aufwand (A22). */
  tooltip: (b: TimelineBar, heute: string, primaryGroup?: string) => {
    const c = b.card;
    return [
      b.isDuplicate && primaryGroup !== undefined ? alsoUnder(primaryGroup) : null,
      `${c.displayNumber} ${c.title}`,
      privateTeam(b),
      [c.companyShortCode, c.customers.map((k) => k.name).join(", ")].filter((t) => t !== "").join(" · "),
      assigneePopup(c),
      stateLine(b, heute),
      ...effortLines(shownEffort(c), "popup"),
      c.resubmitDate === null ? null
        : `Wiedervorlage ${tagBeschriftung(c.resubmitDate)}${c.resubmitTime === null ? "" : ` ${c.resubmitTime}`}`,
      `Anlage ${tagBeschriftung(berlinerTag(c.createdAt))}`,
      c.dueDate !== null && c.resubmitDate !== null && c.dueDate < c.resubmitDate ? DUE_BEFORE_RESUBMIT : null,
      childrenLine(b),
    ].filter((z): z is string => z !== null && z !== "").join("\n");
  },
} as const;
